import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { Dialog } from '../../components/Dialog.js';
import { useToast } from '../../hooks/useToast.js';
import type { MealType, Recipe, RecipeInput, Unit } from '../../api/types.js';
import { createRecipe, updateRecipe } from '../../api/recipes.js';
import { listIngredients } from '../../api/ingredients.js';
import {
  computePerPortionMacros,
  type IngredientLike,
} from './nutrition.js';
import { IngredientRowEditor, type IngredientRow } from './IngredientRowEditor.js';
import { IngredientDialog } from './IngredientDialog.js';

const MEAL_OPTIONS: ReadonlyArray<{ value: MealType; label: string }> = [
  { value: 'breakfast', label: '🌅 Breakfast' },
  { value: 'lunch', label: '☀️ Lunch' },
  { value: 'dinner', label: '🌙 Dinner' },
  { value: 'snack', label: '🍿 Snack' },
];

interface RecipeFormProps {
  open: boolean;
  onClose: () => void;
  onSaved: (recipe: Recipe) => void;
  editing: Recipe | null;
}

interface FormState {
  title: string;
  meal_type: MealType;
  portions: number;
  prep_time_minutes: number;
  instructions: string;
  source_url: string;
  plannable: boolean;
  rows: IngredientRow[];
}

function emptyRow(): IngredientRow {
  return { ingredientId: null, amount: 0, unit: 'g' };
}

function fromRecipe(r: Recipe): FormState {
  return {
    title: r.title,
    meal_type: r.meal_type,
    portions: r.portions,
    prep_time_minutes: r.prep_time_minutes,
    instructions: r.instructions,
    source_url: r.source_url ?? '',
    plannable: r.plannable,
    rows: r.ingredients.map((l) => ({
      ingredientId: l.ingredient_id,
      amount: l.amount,
      unit: l.unit,
    })),
  };
}

function initialForm(): FormState {
  return {
    title: '',
    meal_type: 'lunch',
    portions: 1,
    prep_time_minutes: 15,
    instructions: '',
    source_url: '',
    plannable: true,
    rows: [emptyRow()],
  };
}

export function RecipeForm({
  open,
  onClose,
  onSaved,
  editing,
}: RecipeFormProps): ReactElement | null {
  const toast = useToast();
  const [state, setState] = useState<FormState>(initialForm());
  const [submitting, setSubmitting] = useState(false);
  const [ingredientMap, setIngredientMap] = useState<Map<number, IngredientLike>>(new Map());
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createSeedName, setCreateSeedName] = useState('');
  const [pendingRowIndex, setPendingRowIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setState(editing ? fromRecipe(editing) : initialForm());
  }, [open, editing]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    listIngredients({}).then((rows) => {
      if (cancelled) return;
      const next = new Map<number, IngredientLike>();
      for (const r of rows) {
        next.set(r.id, {
          default_unit_weight_g: r.default_unit_weight_g,
          kcal_per_100: r.kcal_per_100,
          protein_per_100: r.protein_per_100,
          carbs_per_100: r.carbs_per_100,
          fat_per_100: r.fat_per_100,
        });
      }
      setIngredientMap(next);
    }).catch(() => {
      // ignored
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const inputStyle = {
    background: 'var(--bg-primary, transparent)',
    borderColor: 'var(--border)',
    color: 'var(--text-main)',
  };

  function num(v: string, fallback = 1): number {
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }

  function setRow(index: number, next: IngredientRow): void {
    setState((s) => {
      const rows = s.rows.slice();
      rows[index] = next;
      return { ...s, rows };
    });
    if (next.ingredientId !== null && !ingredientMap.has(next.ingredientId)) {
      listIngredients({}).then((rows) => {
        const m = new Map<number, IngredientLike>();
        for (const r of rows) {
          m.set(r.id, {
            default_unit_weight_g: r.default_unit_weight_g,
            kcal_per_100: r.kcal_per_100,
            protein_per_100: r.protein_per_100,
            carbs_per_100: r.carbs_per_100,
            fat_per_100: r.fat_per_100,
          });
        }
        setIngredientMap(m);
      }).catch(() => undefined);
    }
  }

  function removeRow(index: number): void {
    setState((s) => ({ ...s, rows: s.rows.filter((_, i) => i !== index) }));
  }

  function addRow(): void {
    setState((s) => ({ ...s, rows: [...s.rows, emptyRow()] }));
  }

  const validRows = state.rows.filter(
    (r) => r.ingredientId !== null && r.amount > 0,
  );
  const previewMacros = computePerPortionMacros(
    state.portions,
    validRows
      .map((r) => {
        const ing = r.ingredientId !== null ? ingredientMap.get(r.ingredientId) : null;
        if (!ing || r.ingredientId === null) return null;
        return {
          amount: r.amount,
          unit: r.unit,
          ingredient: ing,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null),
  );

  async function onSubmit(): Promise<void> {
    const title = state.title.trim();
    if (title.length === 0) {
      toast.push('error', 'Title is required');
      return;
    }
    if (state.instructions.trim().length === 0) {
      toast.push('error', 'Instructions are required');
      return;
    }
    if (state.portions < 1) {
      toast.push('error', 'Portions must be ≥ 1');
      return;
    }
    if (state.prep_time_minutes < 1) {
      toast.push('error', 'Prep time must be ≥ 1');
      return;
    }
    const ingredientsPayload = state.rows
      .filter((r) => r.ingredientId !== null && r.amount > 0)
      .map((r) => ({
        ingredient_id: r.ingredientId as number,
        amount: r.amount,
        unit: r.unit as Unit,
      }));
    if (ingredientsPayload.length === 0) {
      toast.push('error', 'Add at least one ingredient');
      return;
    }
    const payload: RecipeInput = {
      title,
      meal_type: state.meal_type,
      portions: state.portions,
      prep_time_minutes: state.prep_time_minutes,
      instructions: state.instructions,
      source_url: state.source_url.trim() === '' ? null : state.source_url.trim(),
      ingredients: ingredientsPayload,
      plannable: state.plannable,
    };
    setSubmitting(true);
    try {
      const saved = editing
        ? await updateRecipe(editing.id, payload)
        : await createRecipe(payload);
      toast.push('success', editing ? 'Recipe updated' : 'Recipe saved');
      onSaved(saved);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.push('error', msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Recipe' : 'New Recipe'}
    >
      <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto pr-1">
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--text-muted)' }}>Title</span>
          <input
            type="text"
            value={state.title}
            onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))}
            className="rounded-md border px-3 py-2 text-sm"
            style={inputStyle}
            data-testid="recipe-title"
          />
        </label>

        <div className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--text-muted)' }}>Meal type</span>
          <div
            className="grid grid-cols-4 gap-1 rounded-md border p-1"
            style={{ borderColor: 'var(--border)' }}
          >
            {MEAL_OPTIONS.map((opt) => {
              const active = opt.value === state.meal_type;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setState((s) => ({ ...s, meal_type: opt.value }))}
                  className="rounded-md px-2 py-1 text-xs"
                  style={{
                    background: active ? 'var(--accent)' : 'transparent',
                    color: active ? 'var(--bg-primary, #fff)' : 'var(--text-main)',
                    fontWeight: active ? 600 : 400,
                  }}
                  data-testid={`meal-${opt.value}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={state.plannable}
            onChange={(e) => setState((s) => ({ ...s, plannable: e.target.checked }))}
            data-testid="recipe-plannable"
          />
          <span style={{ color: 'var(--text-main)' }}>
            Include in auto-planner
          </span>
          <span style={{ color: 'var(--text-muted)' }} className="text-xs">
            (uncheck to hide from generated meal plans)
          </span>
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--text-muted)' }}>Portions</span>
            <input
              type="number"
              min="1"
              value={state.portions}
              onChange={(e) =>
                setState((s) => ({ ...s, portions: Math.max(1, num(e.target.value, 1)) }))
              }
              className="rounded-md border px-3 py-2 text-sm"
              style={inputStyle}
              data-testid="recipe-portions"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--text-muted)' }}>Prep time (min)</span>
            <input
              type="number"
              min="1"
              value={state.prep_time_minutes}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  prep_time_minutes: Math.max(1, num(e.target.value, 1)),
                }))
              }
              className="rounded-md border px-3 py-2 text-sm"
              style={inputStyle}
              data-testid="recipe-prep"
            />
          </label>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--text-muted)' }}>
            Instructions (one step per line)
          </span>
          <textarea
            value={state.instructions}
            onChange={(e) => setState((s) => ({ ...s, instructions: e.target.value }))}
            rows={4}
            className="rounded-md border px-3 py-2 text-sm"
            style={inputStyle}
            data-testid="recipe-instructions"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--text-muted)' }}>Source URL (optional)</span>
          <input
            type="url"
            value={state.source_url}
            onChange={(e) => setState((s) => ({ ...s, source_url: e.target.value }))}
            className="rounded-md border px-3 py-2 text-sm"
            style={inputStyle}
          />
        </label>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
              Ingredients
            </span>
            <button
              type="button"
              onClick={addRow}
              className="rounded-md border px-3 py-1 text-xs"
              style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
              data-testid="ingredient-add"
            >
              + Add ingredient
            </button>
          </div>
          <div className="flex flex-col gap-2">
            {state.rows.map((row, idx) => (
              <IngredientRowEditor
                key={idx}
                row={row}
                index={idx}
                onChange={(next) => setRow(idx, next)}
                onRemove={() => removeRow(idx)}
                onCreateRequested={(name) => {
                  setCreateSeedName(name);
                  setPendingRowIndex(idx);
                  setCreateDialogOpen(true);
                }}
              />
            ))}
          </div>
          <div
            className="rounded-md border p-2 text-xs"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-muted)',
              background: 'var(--bg-primary, transparent)',
            }}
            data-testid="recipe-preview-macros"
          >
            Per portion preview ·{' '}
            {Math.round(previewMacros.kcal)} kcal · P {previewMacros.protein_g.toFixed(1)}g ·{' '}
            C {previewMacros.carbs_g.toFixed(1)}g · F {previewMacros.fat_g.toFixed(1)}g
          </div>
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--border)', color: 'var(--text-main)', minHeight: 40 }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onSubmit()}
            disabled={submitting}
            className="rounded-md border px-3 py-2 text-sm font-medium"
            style={{
              background: 'var(--accent)',
              borderColor: 'var(--accent)',
              color: 'var(--bg-primary, #fff)',
              minHeight: 40,
              opacity: submitting ? 0.6 : 1,
            }}
            data-testid="recipe-save"
          >
            💾 Save Recipe
          </button>
        </div>
      </div>

      <IngredientDialog
        open={createDialogOpen}
        initialName={createSeedName}
        onClose={() => setCreateDialogOpen(false)}
        onSaved={(ing) => {
          setIngredientMap((m) => {
            const next = new Map(m);
            next.set(ing.id, {
              default_unit_weight_g: ing.default_unit_weight_g,
              kcal_per_100: ing.kcal_per_100,
              protein_per_100: ing.protein_per_100,
              carbs_per_100: ing.carbs_per_100,
              fat_per_100: ing.fat_per_100,
            });
            return next;
          });
          if (pendingRowIndex !== null) {
            setRow(pendingRowIndex, {
              ingredientId: ing.id,
              amount: 0,
              unit: ing.default_unit,
            });
          }
        }}
      />
    </Dialog>
  );
}